import { captureMessage } from '@sentry/react';
import {
  ActorType,
  Conversation,
  ForumThread,
  ForumThreadMsg,
  IndexMetadata,
  Language,
  RecordSummary,
  RecordSummaryViewFragment,
  RecordType,
  RecordViewFragment,
  SurveyResponse,
} from 'generated/graphql';
import { Feedback, FeedbackContentType } from 'v2/core/entities/feedback';
import {
  ConversationMessage,
  FeedbackContentAudioConversation,
  FeedbackContentConversation,
  FeedbackContentForum,
  FeedbackContentSurvey,
  ForumContent,
  ForumMsg,
} from 'v2/core/entities/feedback/content';

const mapFeedbackContent = (
  record: RecordViewFragment,
): Feedback['content'] => {
  const isTranslated = record.language?.iso6393 !== EnIsoLabel;
  switch (record.type) {
    case RecordType.Conversation:
      return {
        ...mapConversationContent(
          record.conversation,
          record.language,
          record.conversationEn,
          record.summary,
          record.id,
        ),
        isTranslated,
      };
    case RecordType.Review:
      return {
        type: FeedbackContentType.Review,
        textEn: record.textEN || record.text,
        textOriginal:
          record.language?.iso6393 !== EnIsoLabel ? record.text : undefined,
        isTranslated,
        summary: mapRecordSummary(record.summary),
      };
    case RecordType.Survey:
      return {
        ...mapSurveyContent(
          record.surveyResponse,
          record.language,
          record.surveyResponseEn,
          record.summary,
          record.id,
        ),
        isTranslated,
      };
    case RecordType.ForumThread:
      return {
        ...mapForumMessageContent(
          record.forumThread,
          record.language,
          record.forumThreadEn,
          record.summary,
          record.id,
        ),
        isTranslated,
      };
    case RecordType.AudioRecording:
      return {
        ...mapAudioConversationContent(
          record.audioConversation,
          record.language,
          record.audioConversationEn,
          record.id,
        ),
        isTranslated,
      };
    default:
      throw Error(`Invalid type ${record.type} for record: ${record.id}`);
  }
};

const mapSurveyContent = (
  survey: SurveyResponse | undefined,
  language: Language | undefined,
  surveyEn: SurveyResponse | undefined,
  summary: RecordSummaryViewFragment | undefined,
  recordID: string,
): Omit<FeedbackContentSurvey, 'isTranslated'> => {
  const eSurveyEn = surveyEn || survey;
  const originalSurvey = language?.iso6393 !== EnIsoLabel ? survey : undefined;
  if (!eSurveyEn) {
    captureMessage(
      `Survey response not defined for record: ${recordID}`,
      'error',
    );
  }

  return {
    type: FeedbackContentType.Survey,
    responsesEn:
      eSurveyEn?.responses.map((r) => ({
        question: r.question,
        answer: r.answer,
        selectedOptions: r.selectedOptions,
      })) ?? [],
    responsesOriginal: originalSurvey?.responses.map((r) => ({
      question: r.question,
      answer: r.answer,
      selectedOptions: r.selectedOptions,
    })),
    summary: mapRecordSummary(summary),
  };
};

export const EnIsoLabel = 'eng';

const mapForumThreadMsg = (forumThreadMsg: ForumThreadMsg): ForumMsg => {
  return {
    text: forumThreadMsg.text,
    actorID: forumThreadMsg.actorID,
  };
};

const mapForumThread = (
  forumThread: ForumThread | undefined,
  recordID: string,
): ForumContent => {
  const predicted = forumThread?.latestPost ?? forumThread?.header;
  if (!predicted) {
    captureMessage(
      `Forum thread latest post not defined for record: ${recordID}`,
      'error',
    );
  }

  const isHeaderPresent = !!forumThread?.latestPost;

  return {
    title: forumThread?.title,
    header: forumThread?.header
      ? mapForumThreadMsg(forumThread.header)
      : undefined,
    predicted: predicted
      ? mapForumThreadMsg(predicted)
      : {
          text: '',
          actorID: '',
        },
    isHeaderPresent,
    parentMsgs: forumThread?.intermediatePosts?.map(mapForumThreadMsg),
  };
};
const mapForumMessageContent = (
  forumThread: ForumThread | undefined,
  language: Language | undefined,
  forumThreadEn: ForumThread | undefined,
  summary: RecordSummaryViewFragment | undefined,
  recordID: string,
): Omit<FeedbackContentForum, 'isTranslated'> => {
  if (!forumThread) {
    captureMessage(`Forum thread not defined for record: ${recordID}`, 'error');
  }

  const eConvEn = forumThreadEn || forumThread;
  return {
    type: FeedbackContentType.Forum,
    forumContentEn: mapForumThread(eConvEn, recordID),
    forumContentOriginal:
      language?.iso6393 !== EnIsoLabel
        ? mapForumThread(forumThread, recordID)
        : undefined,
    summary: mapRecordSummary(summary),
  };
};

const mapConversationContent = (
  conversation: Conversation | undefined,
  language: Language | undefined,
  conversationEn: Conversation | undefined,
  summary: RecordSummaryViewFragment | undefined,
  recordID: string,
): Omit<FeedbackContentConversation, 'isTranslated'> => {
  if (!conversation) {
    captureMessage(`Conversation not defined for record: ${recordID}`, 'error');
  }

  const eConvEn = conversationEn || conversation;
  return {
    type: FeedbackContentType.Conversation,
    conversationEn: mapConversation(eConvEn, recordID),
    conversationOriginal:
      language?.iso6393 !== EnIsoLabel
        ? mapConversation(conversation, recordID)
        : undefined,
    summary: mapRecordSummary(summary),
  };
};

const mapConversation = (
  conversation: Conversation | undefined,
  recordID: string,
): FeedbackContentConversation['conversationEn'] => {
  if (!conversation?.msgs) {
    captureMessage(
      `Conversation messsage units not defined for record: ${recordID}`,
      'error',
    );
  }

  return (
    conversation?.msgs?.map((message) => ({
      actor: mapActorType(message.actor),
      actorID: message.actorID,
      text: message.text,
    })) ?? []
  );
};

const mapAudioConversation = (
  audioConversation: RecordViewFragment['audioConversation'] | undefined,
  recordID: string,
): FeedbackContentAudioConversation['audioConversationEn'] => {
  if (!audioConversation?.units) {
    captureMessage(
      `Audio conversation units not defined for record: ${recordID}`,
      'error',
    );
  }
  return (
    audioConversation?.units?.map((messageUnit) => ({
      actor: mapActorType(messageUnit.actorType),
      actorName: messageUnit.actor,
      text: messageUnit.text,
      metadata: {
        startTime: messageUnit.metadata.startTime / 1000,
        endTime: messageUnit.metadata.endTime / 1000,
      },
    })) ?? []
  );
};

const mapAudioConversationContent = (
  audioConversation: RecordViewFragment['audioConversation'] | undefined,
  language: Language | undefined,
  audioConversationEn: RecordViewFragment['audioConversation'] | undefined,
  recordID: string,
): Omit<FeedbackContentAudioConversation, 'isTranslated'> => {
  if (!audioConversation) {
    captureMessage(
      `Audio conversation not defined for record: ${recordID}`,
      'error',
    );
  }
  const eConvEn = audioConversationEn || audioConversation;
  return {
    type: FeedbackContentType.Audio,
    audioConversationEn: mapAudioConversation(eConvEn, recordID),
    audioConversationOriginal:
      language?.iso6393 !== EnIsoLabel
        ? mapAudioConversation(audioConversation, recordID)
        : undefined,
    audioFile: audioConversation?.audioFileS3 ?? '',
  };
};

const mapActorType = (actor: ActorType): ConversationMessage['actor'] => {
  switch (actor) {
    case ActorType.Agent:
      return 'Agent';
    case ActorType.Bot:
      return 'Bot';
    case ActorType.User:
      return 'User';
  }
};

export const mapRecordSummary = (
  summaryFragment: RecordSummaryViewFragment | undefined,
): RecordSummary => {
  return {
    ...summaryFragment,
    units:
      summaryFragment?.units?.map((unit) => ({
        ...unit,
        metadata: {
          ...unit.metadata,
          location: unit.metadata?.location?.map((location) => {
            if (location.__typename === 'IndexTypeKeywordSummary') {
              return {
                ...location,
                startIdx: 0,
                endIdx: 0,
              };
            }
            return location;
          }) as IndexMetadata[],
        },
      })) ?? [],
  };
};

export default mapFeedbackContent;
